/**
 * The member-facing words for invites, the waitlist and influence (GH-864, DEC-051), in one place
 * for donor-web and iOS to use verbatim. `apps/membership-copy.test.ts` holds the Swift twin
 * (`apps/ios/GiveHere/Models/MembershipCopy.swift`) to these exact strings, functions by their
 * template.
 *
 * Rules the test enforces:
 *   - every on-screen label (`MEMBERSHIP_LABELS`) is five words or fewer; the long lines sit behind
 *     an ⓘ;
 *   - no faith's vocabulary; points are never written like money; nothing is ever "lost";
 *   - scarcity ("invites left", "in line", "waiting") appears on membership surfaces only — never on
 *     a give, checkout, payment or embed surface (DEC-051's design exception).
 *
 * Numbers arrive pre-formatted ("1,214"), so both surfaces group digits the same way.
 */
export const MEMBERSHIP_COPY = {
    /* ------------------------------------------------------------ entry and waitlist */
    haveInvite: "Have an invite?",
    enterInviteCode: "Enter your invite code",
    joinWaitlist: "Join the waitlist",
    waitlistPosition: (position) => `You’re #${position} in line`,
    waitingCount: (count) => `Over ${count} waiting`,
    invitedYou: (name) => `${name} invited you`,
    invitedYouAnonymous: "You’re invited",
    /* GH-864h: an invite is accepted only by this tap, never by opening its link. */
    join: "Join",
    welcomeMember: (number) => `Welcome, member #${number}`,
    foundingMember: "Founding member",
    memberNumber: (number) => `Member #${number}`,
    inviteUnavailable: "This invite isn’t available",
    inviteUnavailableBody: "It may have been used or withdrawn. Ask the person who sent it for a new one, or join the waitlist.",
    eventCodeExpired: "This code has expired",
    eventCodeExpiredBody: "You’re on the waitlist. Ask a member for an invite, or scan the code on the screen again.",
    /* --------------------------------------------------------------------- invites */
    invitesTitle: "Your invites",
    invitesLeftOne: "1 invite left",
    invitesLeft: (count) => `${count} invites left`,
    newInvite: "Invite a friend",
    shareInvite: "Share",
    shareAgain: "Share again",
    revokeInvite: "Revoke",
    inviteRedeemed: "Joined",
    inviteRevoked: "Revoked",
    inviteExpired: "Expired",
    /* GH-864g: moved here from the app (GH-864d) so both surfaces say them the same way. */
    inviteSent: (date) => `Sent ${date}`,
    inviteExpires: (date) => `Expires ${date}`,
    revokeInviteConfirm: "Revoke this invite?",
    invitesPaused: "Invites are paused",
    notAvailableYet: "Not available yet",
    invitesExplanationTitle: "How invites work",
    neverMessageFriends: "We never message your friends",
    invitesExplanation: "Each invite works once. We never message your friends — you share the link yourself. You get another invite when someone you invited joins, and three more each time someone you invited gives.",
    /* -------------------------------------------------------------------- influence */
    yourInfluence: "Your influence",
    pointsLabel: "Points",
    directLabel: "From your invites",
    networkLabel: "Your wider network",
    thisWeek: "This week",
    networkThisWeek: "Your network this week",
    weeklyUpdateReady: "Your weekly update is ready",
    fewerThanFiveGivers: "Fewer than 5 givers yet",
    streakWeeks: (weeks) => `${weeks}-week streak`,
    levelLabel: "Level",
    milestonesTitle: "Milestones",
    /* Milestone names (GH-864g, from GH-864d's app-only wording). `founding_member` and the streak
       milestones reuse `foundingMember` and `streakWeeks`. */
    milestoneFirstInviteJoined: "First invite joined",
    milestoneFiveGivers: "Five givers reached",
    milestoneReachedLevel: (level) => `Reached ${level}`,
    howPointsWork: "How points work",
    noCashValue: "No cash value",
    pointsExplanation: "Points reflect giving your invites and share links helped inspire — about one point per dollar given, including by people your invitees invited. They update every Monday, appear only once at least five people have given, and are rounded. Points have no cash value: they can’t be redeemed, exchanged or transferred.",
    /* ---------------------------------------------------------------------- sharing */
    shareWithFriends: "Share with friends",
    shareWhatWeRaised: "Share what we raised",
    copyLink: "Copy link",
    linkCopied: "Link copied",
    /* -------------------------------------------------------------- your preferences */
    showNameOnInvites: "Show my name on invites",
    dontCreditInviter: "Don’t credit my inviter",
    dontCreditInviterExplanation: "When this is on, your gifts never add to the points of the person who invited you, or anyone who invited them.",
    weeklyDigest: "Weekly email update",
};
/** What sits on screen as a label or button — five words or fewer each. */
export const MEMBERSHIP_LABELS = [
    MEMBERSHIP_COPY.haveInvite,
    MEMBERSHIP_COPY.enterInviteCode,
    MEMBERSHIP_COPY.joinWaitlist,
    MEMBERSHIP_COPY.waitlistPosition("1,214"),
    MEMBERSHIP_COPY.waitingCount("1,200"),
    MEMBERSHIP_COPY.invitedYou("Amina R."),
    MEMBERSHIP_COPY.invitedYouAnonymous,
    MEMBERSHIP_COPY.join,
    MEMBERSHIP_COPY.welcomeMember("214"),
    MEMBERSHIP_COPY.foundingMember,
    MEMBERSHIP_COPY.memberNumber("214"),
    MEMBERSHIP_COPY.inviteUnavailable,
    MEMBERSHIP_COPY.eventCodeExpired,
    MEMBERSHIP_COPY.invitesTitle,
    MEMBERSHIP_COPY.invitesLeftOne,
    MEMBERSHIP_COPY.invitesLeft("12"),
    MEMBERSHIP_COPY.newInvite,
    MEMBERSHIP_COPY.shareInvite,
    MEMBERSHIP_COPY.shareAgain,
    MEMBERSHIP_COPY.revokeInvite,
    MEMBERSHIP_COPY.inviteRedeemed,
    MEMBERSHIP_COPY.inviteRevoked,
    MEMBERSHIP_COPY.inviteExpired,
    MEMBERSHIP_COPY.inviteSent("Oct 3"),
    MEMBERSHIP_COPY.inviteExpires("Nov 2"),
    MEMBERSHIP_COPY.revokeInviteConfirm,
    MEMBERSHIP_COPY.invitesPaused,
    MEMBERSHIP_COPY.notAvailableYet,
    MEMBERSHIP_COPY.invitesExplanationTitle,
    MEMBERSHIP_COPY.neverMessageFriends,
    MEMBERSHIP_COPY.yourInfluence,
    MEMBERSHIP_COPY.pointsLabel,
    MEMBERSHIP_COPY.directLabel,
    MEMBERSHIP_COPY.networkLabel,
    MEMBERSHIP_COPY.thisWeek,
    MEMBERSHIP_COPY.networkThisWeek,
    MEMBERSHIP_COPY.weeklyUpdateReady,
    MEMBERSHIP_COPY.fewerThanFiveGivers,
    MEMBERSHIP_COPY.streakWeeks("3"),
    MEMBERSHIP_COPY.levelLabel,
    MEMBERSHIP_COPY.milestonesTitle,
    MEMBERSHIP_COPY.milestoneFirstInviteJoined,
    MEMBERSHIP_COPY.milestoneFiveGivers,
    MEMBERSHIP_COPY.milestoneReachedLevel("Cornerstone"),
    MEMBERSHIP_COPY.howPointsWork,
    MEMBERSHIP_COPY.noCashValue,
    MEMBERSHIP_COPY.shareWithFriends,
    MEMBERSHIP_COPY.shareWhatWeRaised,
    MEMBERSHIP_COPY.copyLink,
    MEMBERSHIP_COPY.linkCopied,
    MEMBERSHIP_COPY.showNameOnInvites,
    MEMBERSHIP_COPY.dontCreditInviter,
    MEMBERSHIP_COPY.weeklyDigest,
];
/**
 * The platform's neutral share-sheet defaults (GH-864b/c build `messages[]` from these, or from an
 * organization's own `share_message`). Server-side only — never an amount, never a faith word.
 */
export const SHARE_MESSAGE_TEMPLATES = {
    invite: (link) => `Join me on GiveHere: ${link}`,
    organization: (organization, link) => `I gave to ${organization} through GiveHere. Join me: ${link}`,
    event: (event, organization, link) => `${event} is raising for ${organization}: ${link}`,
};
//# sourceMappingURL=membership-copy.js.map